import type { AIStore } from '..';
import type { StateCreator } from 'zustand';

import { actionLog } from '@core/store/util';

interface Checkpoint {
  action: string;
  content: Record<string, unknown> | string;
  timestamp: Date;
}

interface AiAgentSliceState {
  checkpoints: Checkpoint[];
  /**
   * Context for pre-populating chat input when navigating from other components.
   */
  context?: {
    title?: string;
    message?: string;
    type?: string;
    /**
     * Hidden context is not shown to the user, but included in the prompt
     */
    hiddenContext?: Record<string, string>;
  };
  curChatId: string | null;
  numMessages: number;
}

interface AiAgentSliceAction {
  addCheckpoint: (checkpoint: { content: Record<string, unknown> | string; action: string }) => number;
  /**
   * Clears context after it has been consumed.
   */
  clearContext: () => void;
  popCheckpoint: () => void;
  resetChatMessages: () => void;
  resetCheckpoints: () => void;
  /**
   * Sets AI context for pre-populating chat input.
   */
  setContext: (context: AiAgentSliceState['context']) => void;
  setNumMessages: (count: number) => void;
  updateCurChatId: (chatId: string, messageCount: number) => void;
}

export interface AiAgentSlice {
  agent: AiAgentSliceAction & AiAgentSliceState;
}

const initialState: AiAgentSliceState = {
  checkpoints: [],
  curChatId: null,
  numMessages: 0,
};

export const createAiAgentSlice: StateCreator<
  AIStore,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  AiAgentSlice
> = (set, get) => ({
  agent: {
    ...initialState,

    addCheckpoint: checkpoint => {
      const newCheckpoint: Checkpoint = {
        ...checkpoint,
        timestamp: new Date(),
      };

      set(
        state => {
          state.agent.checkpoints.push(newCheckpoint);
        },
        false,
        actionLog('addCheckpoint', checkpoint),
      );

      return get().agent.checkpoints.length;
    },

    clearContext: () => {
      set(
        state => {
          state.agent.context = {};
        },
        false,
        actionLog('clearContext'),
      );
    },

    setNumMessages: (count: number) => {
      set(
        state => {
          state.agent.numMessages = count;
        },
        false,
        actionLog('setNumMessages', count),
      );
    },

    popCheckpoint: () => {
      set(
        state => {
          state.agent.checkpoints.pop();
        },
        false,
        actionLog('popCheckpoint'),
      );
    },

    resetCheckpoints: () => {
      set(
        state => {
          state.agent.checkpoints = [];
        },
        false,
        actionLog('resetCheckpoints'),
      );
    },

    resetChatMessages: () => {
      set(
        state => {
          state.agent.curChatId = null;
          state.agent.numMessages = 0;
        },
        false,
        actionLog('resetChatMessages'),
      );
    },

    setContext: context => {
      set(
        state => {
          state.agent.context = context;
        },
        false,
        actionLog('setContext', { context }),
      );
    },

    updateCurChatId: (chatId: string, messageCount: number) => {
      set(
        state => {
          state.agent.curChatId = chatId;
          state.agent.numMessages = messageCount;
        },
        false,
        actionLog('updateCurChatId', { chatId, messageCount }),
      );
    },
  },
});
